import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Clipboard, Linking, TouchableOpacity } from 'react-native';
import { ActionSheet } from '@shared/components/ActionSheet';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { COPY_TOOLTIP_DURATION_MS } from '@shared/components/UrlActionButtons';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { useShowUrlActionSheet } from '@shared/components/link';
import { MBTextInput } from '@shared/components/textInput';
import { TruncateLink } from '@shared/components/truncate';
import { UrlField } from '@shared/data/entity/fields/UrlField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { getComponentLabel, getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { Colors, getAnchorName, grid } from '@shared/styles';
import { useBoolean, useMountedRef } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { validateUrl } from '@shared/util/validate/url';
import { EditFieldContainer } from '../EditFieldContainer';
import { UrlEditFieldProps } from './UrlEditFieldProps';
import { addPrefixIfNeeded } from './addPrefixIfNeeded';

export function UrlEditFieldMobile({
  field,
  component,
  updateField,
  error,
  allowClear,
  style,
}: UrlEditFieldProps) {
  const [value, setValue] = useState(field?.url ?? '');
  const hasValue = !isEmpty(value);
  const isReadOnly = !!component.readonly;
  const urlActionSheet = useShowUrlActionSheet({ url: value });
  const [isModalOpen, openModal, closeModal] = useBoolean();
  const modalTitle = getComponentLabel(component) ?? '';
  const anchorName = getAnchorName('url-edit-field', component.id);

  useEffect(() => {
    const updatedField = !isEmpty(value) ? new UrlField([{ url: value.trim() }]) : null;
    updateField(component.id, updatedField);
  }, [value, updateField, component.id]);

  useEffect(() => {
    if (!isModalOpen) {
      setValue(addPrefixIfNeeded);
    }
  }, [isModalOpen]);

  const onClear = useCallback(() => {
    setValue('');
  }, []);

  const showClearButton = hasValue && !isReadOnly && allowClear;
  const placeholder = getComponentPlaceholder(component) ?? '';
  const previewText = value || placeholder;
  const placeholderStyle = { color: Colors.v2.gray50 };
  const responsiveForms = useResponsiveFormContext();

  return (
    <EditFieldContainer
      component={component}
      showLabel={responsiveForms || hasValue}
      error={error}
      showClearButton={showClearButton}
      onClear={onClear}
      style={style}
      anchorName={anchorName}
    >
      <TouchableOpacity
        onPress={isReadOnly ? urlActionSheet.openMenu : openModal}
        onLongPress={urlActionSheet.openMenu}
      >
        <View paddingRight={2}>
          <TruncateLink
            href={previewText}
            showProtocol={true}
            textStyle={[!hasValue && placeholderStyle]}
          />
        </View>
        {isModalOpen && (
          <UrlEditSheet
            value={value}
            setValue={setValue}
            closeModal={closeModal}
            modalTitle={modalTitle}
            placeholder={placeholder}
            anchorName={anchorName}
          />
        )}
        {urlActionSheet.isOpen && (
          <ActionSheet
            anchorName={anchorName}
            title={urlActionSheet.title}
            onClose={urlActionSheet.closeMenu}
            options={urlActionSheet.menuOptions}
          />
        )}
      </TouchableOpacity>
    </EditFieldContainer>
  );
}

function UrlEditSheet({
  value,
  setValue,
  closeModal,
  modalTitle,
  placeholder,
  anchorName,
}: {
  value: string;
  setValue: (value: string) => void;
  closeModal: VoidFunction;
  modalTitle: string;
  placeholder?: string;
  anchorName: string;
}) {
  return (
    <AnchoredSheet
      onClose={closeModal}
      anchorName={anchorName}
      renderTitleBar={() => {
        return (
          <AnchoredSheetTitleBar
            onRightIconPress={closeModal}
            title={modalTitle}
            rightIcon="exit"
            rightIconColor={Colors.v2.purple}
            rightIconSize={grid(4)}
          />
        );
      }}
    >
      <View padding={2}>
        <MBTextInput
          multiline={true}
          inputMode="url"
          textContentType="URL"
          keyboardType="url"
          value={value}
          onChangeText={setValue}
          placeholder={placeholder}
          autoCapitalize="none"
          autoFocus={true}
          style={{
            fontSize: 16,
            lineHeight: 24,
            backgroundColor: Colors.v2.gray05,
            maxHeight: 250,
          }}
        />
        <UrlEditSheetButtons value={value} />
      </View>
    </AnchoredSheet>
  );
}

function UrlEditSheetButtons({ value }: { value: string }) {
  const mounted = useMountedRef();
  const openLinkLabel = i18n('workflowTemplate.component.url.actionButtonsSheet.openInBrowser');
  const copyLinkLabel = i18n('workflowTemplate.component.url.actionButtons.copyToClipboard');
  const copiedLinkLabel = i18n('workflowTemplate.component.url.actionButtons.copiedToClipboard');
  const [copyButtonLabel, setCopyButtonLabel] = useState(copyLinkLabel);
  const href = addPrefixIfNeeded(value);
  const isValid = validateUrl(href);

  const copyToClipboard = () => {
    Clipboard.setString(href);
    setCopyButtonLabel(copiedLinkLabel);
    setTimeout(() => {
      if (mounted.current) {
        setCopyButtonLabel(copyLinkLabel);
      }
    }, COPY_TOOLTIP_DURATION_MS);
  };

  const openUrl = () => {
    Linking.openURL(href);
  };

  const opacity = isValid ? 1 : 0.3;

  return (
    <View row paddingTop={3} style={{ justifyContent: 'space-between', opacity }}>
      <View flex style={{ flexBasis: '55%' }}>
        <MBButton
          leftIcon="link"
          leftIconSize={grid(4)}
          backgroundColor={Colors.v2.gray05}
          titleColor={Colors.v2.purple}
          title={openLinkLabel}
          onPress={isValid ? openUrl : undefined}
          style={{ flex: 1, paddingLeft: grid(1) }}
        />
      </View>
      <View flex style={{ flexBasis: '5%' }} />
      <View flex style={{ flexBasis: '40%' }}>
        <MBButton
          leftIcon="squares-stacked"
          leftIconSize={grid(4)}
          backgroundColor={Colors.v2.gray05}
          titleColor={Colors.v2.purple}
          title={copyButtonLabel}
          onPress={isValid ? copyToClipboard : undefined}
          style={{ flex: 1, paddingLeft: grid(1) }}
        />
      </View>
    </View>
  );
}
